import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useUrlState } from '@/hooks/useUrlState';
import {
  Archive,
  ArrowDown,
  ArrowUp,
  Ban,
  ChevronDown,
  ChevronRight,
  Database,
  Eye,
  EyeOff,
  FileText,
  FolderInput,
  Globe,
  GlobeLock,
  History,
  Import,
  Link2,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Unlock,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { PageBreadcrumbs, ancestorsOf, type Crumb } from '@/components/ui/PageBreadcrumbs';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { JSONContent } from '@tiptap/react';

import {
  BlockContent,
  BlockEditor,
  SaveIndicator,
  useAutosave,
  type BlockEditorHandle,
  type MentionItem,
} from '@/components/ui/block-editor';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { pagesApi } from '../api';
import { projectPath } from '../paths';
import { makeProjectPageProvider } from '../pageProvider';
import { ancestorChain, defaultPageId, treeRoots } from '../pageTree';
import { ConfluenceImportDialog } from '@/features/imports';
import { pagesHooks, projectMembersHooks, usePageAction, usePageVersions } from '../queries';
import { KbSyncBadge, useSyncProject, useSyncSource, useSourceStatus } from '@/features/knowledge_base';
import type { Project, ProjectMember, WikiPage, WikiPageVersion } from '../types';
import { projectMemberName } from '../select';

function rowsOf<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

type Tab = 'public' | 'private' | 'archived';

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

// Pointer position at drag start — one drag at a time, so a module-level var is
// enough to measure travel and ignore accidental micro-drags.
let dragOrigin: { x: number; y: number } | null = null;

export function ProjectPagesTab({ project }: { project: Project }) {
  // Fetch the whole tree (not the default 20/page) — the sidebar + embeds need
  // every page, else children past the first page silently vanish.
  const query = pagesHooks.useList({ project: project.id, page_size: 1000 });
  const create = pagesHooks.useCreate();
  const qc = useQueryClient();
  const syncProject = useSyncProject();
  const [importOpen, setImportOpen] = useState(false);
  // Knowledge-Base sync + Confluence import are staff-only infra (BE denies
  // guests); hide the affordances so clients never see them.
  const isGuest = useSession((s) => s.role) === 'guest';
  // KB controls also disappear when the project's KB sync is off (DEV13-113).
  const showKb = !isGuest && project.kb_sync_enabled;
  // Guests may write only with a member/lead project role (viewer = read-only);
  // staff always can. Gates Add-page + the per-row management menu.
  const canWrite = !isGuest || project.my_role === 'member' || project.my_role === 'lead';
  // Sub-tab + selected page live in the URL (survive reload, back-swipe).
  // `pagesTab` empty = unset → derive from the selected/linked page's own tab.
  const [pagesTab, setPagesTab] = useUrlState<Tab | ''>('pagesTab', '');
  const [pagePub, setPagePub] = useUrlState('page', '', { push: true });
  // Explicit user opens/closes. The first top-level page and the selected page's
  // ancestors are open *by default* (sub-pages show instantly; a reloaded deep
  // link shows its row); `collapsed` records when the user closes a default-open
  // node, so the choice sticks — all derived, no effects.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const all = useMemo(() => rows<WikiPage>(query.data), [query.data]);
  const selectedPage = useMemo(
    () => (pagePub ? (all.find((p) => p.public_id === pagePub) ?? null) : null),
    [all, pagePub],
  );
  const effectiveTab: Tab =
    (pagesTab as Tab) ||
    (selectedPage ? (selectedPage.is_archived ? 'archived' : selectedPage.visibility) : 'public');
  const visible = useMemo(
    () =>
      effectiveTab === 'archived'
        ? all.filter((p) => p.is_archived)
        : all.filter((p) => !p.is_archived && p.visibility === effectiveTab),
    [all, effectiveTab],
  );

  const flat = effectiveTab === 'archived';
  const byId = useMemo(() => new Map(all.map((p) => [p.id, p])), [all]);
  // The first row the sidebar shows (its first top-level page; the first page of
  // the flat archived list) — the default selection and open by default.
  const firstRootId = useMemo(() => defaultPageId(visible, flat), [visible, flat]);

  // The `?page=` selection wins; else the first row the sidebar shows — not
  // `visible[0]`, which is the API's flat order and can be a nested page
  // (DEV13-120).
  const effectiveId =
    selectedPage && visible.some((p) => p.id === selectedPage.id) ? selectedPage.id : firstRootId;
  const selected = all.find((p) => p.id === effectiveId) ?? null;

  const expandedView = useMemo(() => {
    const next = new Set(expanded);
    // Open by default: the first top-level page, and the selected page's
    // ancestors so a reloaded deep link shows (and highlights) its row
    // (DEV13-123) — unless the user closed one by hand.
    const defaults = [firstRootId, ...ancestorChain(byId, byId.get(effectiveId ?? -1)?.parent)];
    for (const id of defaults) if (id != null && !collapsed.has(id)) next.add(id);
    return next;
  }, [expanded, collapsed, firstRootId, byId, effectiveId]);

  const toggleExpand = (id: number) => {
    const open = expandedView.has(id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open) next.delete(id);
      else next.add(id);
      return next;
    });
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [movingPage, setMovingPage] = useState<WikiPage | null>(null);

  function addPage(parent: number | null = null) {
    create.mutate(
      {
        project: project.id,
        title: 'Untitled',
        parent,
        visibility: effectiveTab === 'private' ? 'private' : 'public',
      },
      { onSuccess: (page) => setPagePub(page.public_id) },
    );
  }

  const childrenOf = (parentId: number | null) =>
    all.filter((p) => (p.parent ?? null) === parentId && !p.is_archived).sort((a, b) => a.order - b.order);

  function descendantIds(rootId: number): Set<number> {
    const out = new Set<number>();
    const walk = (id: number) => {
      for (const c of all.filter((p) => p.parent === id)) {
        out.add(c.id);
        walk(c.id);
      }
    };
    walk(rootId);
    return out;
  }

  type MoveItem = { id: number; parent: number | null; order: number; visibility?: string };
  const reorder = usePageAction((items: MoveItem[]) => pagesApi.reorder(items));

  /** Expand the moved page's ancestors + select it, so a move never hides it. */
  function revealMovedPage(page: WikiPage, newParent: number | null) {
    const chain = ancestorChain(byId, newParent);
    if (chain.length) {
      setExpanded((prev) => new Set([...prev, ...chain]));
      setCollapsed((prev) => new Set([...prev].filter((id) => !chain.includes(id))));
    }
    setPagePub(page.public_id);
  }

  /** Apply a batch move atomically, reveal the page, and offer Undo. */
  function applyMove(page: WikiPage, items: MoveItem[], newParent: number | null) {
    const prev: MoveItem = {
      id: page.id,
      parent: page.parent ?? null,
      order: page.order,
      visibility: page.visibility,
    };
    reorder.mutate(items, {
      onSuccess: () => {
        revealMovedPage(page, newParent);
        toast.success(
          <span className="flex items-center gap-2">
            Moved “{page.title || 'Untitled'}”
            <button
              type="button"
              className="cursor-pointer font-medium underline"
              onClick={() => reorder.mutate([prev])}
            >
              Undo
            </button>
          </span>,
        );
      },
    });
  }

  /** Re-parent a page under `newParent` (null = top level), appended last.
   *  A child inherits the new parent's visibility so it can't drift to another tab. */
  function moveToParent(page: WikiPage, newParent: number | null) {
    if (newParent != null && descendantIds(page.id).has(newParent)) return;
    const parent = newParent != null ? all.find((p) => p.id === newParent) : null;
    const order = childrenOf(newParent).filter((p) => p.id !== page.id).length;
    applyMove(
      page,
      [{ id: page.id, parent: newParent, order, ...(parent ? { visibility: parent.visibility } : {}) }],
      newParent,
    );
    setMovingPage(null);
  }

  /** Drag-and-drop: drop onto a row (`inside`) or between rows (`before`/`after`). */
  function handleMove(dragId: number, targetId: number, position: 'before' | 'after' | 'inside') {
    if (dragId === targetId) return;
    const drag = all.find((p) => p.id === dragId);
    const target = all.find((p) => p.id === targetId);
    if (!drag || !target || descendantIds(dragId).has(targetId)) return;

    if (position === 'inside') {
      moveToParent(drag, targetId);
      return;
    }
    const newParent = target.parent ?? null;
    const parent = newParent != null ? all.find((p) => p.id === newParent) : null;
    const siblings = childrenOf(newParent).filter((p) => p.id !== dragId);
    const idx = siblings.findIndex((p) => p.id === targetId);
    const insertAt = position === 'before' ? idx : idx + 1;
    const ordered = [...siblings.slice(0, insertAt), drag, ...siblings.slice(insertAt)];
    const items: MoveItem[] = ordered.map((p, i) =>
      p.id === dragId
        ? { id: p.id, parent: newParent, order: i, ...(parent ? { visibility: parent.visibility } : {}) }
        : { id: p.id, parent: newParent, order: i },
    );
    applyMove(drag, items, newParent);
  }

  /** Menu "Move up/down": swap order with the adjacent sibling (same parent). */
  function moveStep(page: WikiPage, dir: 'up' | 'down') {
    const siblings = childrenOf(page.parent ?? null);
    const idx = siblings.findIndex((p) => p.id === page.id);
    const neighbor = dir === 'up' ? siblings[idx - 1] : siblings[idx + 1];
    if (neighbor) handleMove(page.id, neighbor.id, dir === 'up' ? 'before' : 'after');
  }

  return (
    <div className="flex min-h-[28rem] gap-4">
      {/* Tree pane */}
      <aside className="w-64 shrink-0 space-y-3 border-r pr-3">
        {showKb ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground h-auto w-full justify-start gap-1.5 px-2 py-1 text-xs"
            title="Sync every page in this project to the Knowledge Base"
            onClick={() => syncProject.mutate(project.id)}
            disabled={syncProject.isPending}
          >
            <Database className="size-3.5" /> Sync to Knowledge Base
          </Button>
        ) : null}
        <div className="flex gap-1">
          {(['public', 'private', 'archived'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setPagesTab(t)}
              className={cn(
                'rounded-md px-2 py-1 text-xs font-medium capitalize',
                effectiveTab === t ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {effectiveTab !== 'archived' && canWrite ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => addPage(null)}>
              <Plus className="size-4" /> Add page
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label="Import from Confluence"
              title="Import from Confluence"
              onClick={() => setImportOpen(true)}
            >
              <Import className="size-4" />
            </Button>
          </div>
        ) : null}

        {query.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : visible.length === 0 ? (
          <p className="text-muted-foreground px-1 py-4 text-sm">No {effectiveTab} pages.</p>
        ) : (
          <PageTree
            pages={visible}
            flat={flat}
            selectedId={effectiveId}
            onSelect={(id) => setPagePub(all.find((p) => p.id === id)?.public_id ?? '')}
            expanded={expandedView}
            onToggleExpand={toggleExpand}
            onAddChild={addPage}
            onRequestMove={setMovingPage}
            draggingId={draggingId}
            setDraggingId={setDraggingId}
            onMove={handleMove}
            onMoveStep={moveStep}
            canWrite={canWrite}
            showKb={showKb}
          />
        )}
      </aside>

      {/* Editor pane */}
      <div className="min-w-0 flex-1">
        {selected ? (
          <PageEditor
            key={selected.id}
            project={project}
            page={selected}
            allPages={all}
            onAddChild={addPage}
            onOpenPage={setPagePub}
          />
        ) : (
          <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
            Select or create a page.
          </div>
        )}
      </div>

      <MoveDialog
        page={movingPage}
        excludeIds={movingPage ? new Set([movingPage.id, ...descendantIds(movingPage.id)]) : new Set()}
        pages={all.filter((p) => !p.is_archived)}
        onMove={moveToParent}
        onClose={() => setMovingPage(null)}
      />

      <ConfluenceImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        destination={{ kind: 'project', projectPublicId: project.public_id }}
        onImported={() => qc.invalidateQueries({ queryKey: ['wiki-pages'] })}
      />
    </div>
  );
}

function MoveDialog({
  page,
  excludeIds,
  pages,
  onMove,
  onClose,
}: {
  page: WikiPage | null;
  excludeIds: Set<number>;
  pages: WikiPage[];
  onMove: (page: WikiPage, parent: number | null) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const candidates = pages.filter(
    (p) => !excludeIds.has(p.id) && (p.title || 'Untitled').toLowerCase().includes(q.toLowerCase()),
  );
  return (
    <Dialog open={page !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move “{page?.title || 'Untitled'}”</DialogTitle>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages…" className="h-9" />
        <div className="max-h-72 space-y-0.5 overflow-y-auto">
          <button
            type="button"
            onClick={() => page && onMove(page, null)}
            className="hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
          >
            <span className="text-muted-foreground">↑</span> Top level
          </button>
          {candidates.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => page && onMove(page, p.id)}
              className="hover:bg-muted flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm"
            >
              <span>{p.icon || '📄'}</span>
              <span className="truncate">{p.title || 'Untitled'}</span>
            </button>
          ))}
          {candidates.length === 0 ? (
            <p className="text-muted-foreground px-2 py-2 text-xs">No matching pages.</p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------
interface TreeProps {
  pages: WikiPage[];
  flat: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
  expanded: Set<number>;
  onToggleExpand: (id: number) => void;
  onAddChild: (parentId: number) => void;
  onRequestMove: (page: WikiPage) => void;
  draggingId: number | null;
  setDraggingId: (id: number | null) => void;
  onMove: (dragId: number, targetId: number, position: 'before' | 'after' | 'inside') => void;
  onMoveStep: (page: WikiPage, dir: 'up' | 'down') => void;
  /** Caller may manage pages here (staff, or a member/lead guest). */
  canWrite: boolean;
  /** Staff, and the project's KB sync is on — show KB controls. */
  showKb: boolean;
}

function PageTree(props: TreeProps) {
  const { pages, flat, expanded } = props;

  if (flat) {
    return (
      <div className="space-y-0.5">
        {pages.map((p) => (
          <PageRow key={p.id} page={p} depth={0} hasChildren={false} {...props} />
        ))}
      </div>
    );
  }

  const byParent = new Map<number | null, WikiPage[]>();
  for (const p of pages) {
    const key = p.parent ?? null;
    byParent.set(key, [...(byParent.get(key) ?? []), p]);
  }
  // Orphans (parent archived) are roots here and re-nest on restore — the parent
  // FK is untouched.
  const roots = treeRoots(pages);
  const render = (parent: number | null, depth: number): React.ReactNode => {
    const sibs = parent === null ? roots : (byParent.get(parent) ?? []);
    return sibs.map((p, i) => {
      const children = byParent.get(p.id) ?? [];
      const isCollapsed = !expanded.has(p.id);
      return (
        <div key={p.id}>
          <PageRow
            page={p}
            depth={depth}
            hasChildren={children.length > 0}
            isCollapsed={isCollapsed}
            isFirst={i === 0}
            isLast={i === sibs.length - 1}
            {...props}
          />
          {children.length > 0 && !isCollapsed ? render(p.id, depth + 1) : null}
        </div>
      );
    });
  };
  return <div className="space-y-0.5">{render(null, 0)}</div>;
}

function PageRow({
  page,
  depth,
  hasChildren,
  isCollapsed,
  flat,
  selectedId,
  onSelect,
  onToggleExpand,
  onAddChild,
  onRequestMove,
  draggingId,
  setDraggingId,
  onMove,
  onMoveStep,
  canWrite,
  showKb,
  isFirst,
  isLast,
}: TreeProps & {
  page: WikiPage;
  depth: number;
  hasChildren: boolean;
  isCollapsed?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  const update = pagesHooks.useUpdate();
  const isSelected = page.id === selectedId;
  // Bring the selected row into the sidebar's view — e.g. a reloaded deep link
  // to a page far down the tree (DEV13-123). `nearest` leaves a visible row be.
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSelected) rowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [isSelected]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(page.title);
  const [dropHint, setDropHint] = useState<'before' | 'after' | 'inside' | null>(null);
  const draggable = !flat && !page.is_archived;

  function startEditing() {
    if (page.is_archived) return;
    setDraft(page.title);
    setEditing(true);
  }

  function commit() {
    const next = draft.trim();
    if (next && next !== page.title) update.mutate({ id: page.public_id, input: { title: next } });
    setEditing(false);
  }

  return (
    <div
      ref={rowRef}
      aria-current={isSelected ? 'page' : undefined}
      draggable={draggable && !editing}
      onDragStart={(e) => {
        dragOrigin = { x: e.clientX, y: e.clientY };
        setDraggingId(page.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => {
        setDraggingId(null);
        setDropHint(null);
      }}
      onDragOver={(e) => {
        if (draggingId == null || draggingId === page.id || !draggable) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        // Narrow "inside" band (middle 30%) so most drops reorder, not re-parent.
        setDropHint(y < rect.height * 0.35 ? 'before' : y > rect.height * 0.65 ? 'after' : 'inside');
      }}
      onDragLeave={() => setDropHint(null)}
      onDrop={(e) => {
        e.preventDefault();
        // Ignore an accidental micro-drag (press + tiny move + release).
        const moved = dragOrigin
          ? Math.hypot(e.clientX - dragOrigin.x, e.clientY - dragOrigin.y)
          : Infinity;
        if (moved >= 6 && draggingId != null && dropHint) onMove(draggingId, page.id, dropHint);
        dragOrigin = null;
        setDropHint(null);
        setDraggingId(null);
      }}
      className={cn(
        'group flex cursor-pointer items-center gap-0.5 rounded pr-1',
        isSelected ? 'bg-muted' : 'hover:bg-muted/50',
        dropHint === 'inside' && 'ring-primary ring-1',
        dropHint === 'before' && 'border-primary border-t-2',
        dropHint === 'after' && 'border-primary border-b-2',
      )}
      style={{ paddingLeft: `${depth * 14}px` }}
    >
      {hasChildren ? (
        <button
          type="button"
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          onClick={() => onToggleExpand(page.id)}
          className="text-muted-foreground hover:text-foreground shrink-0 rounded p-0.5"
        >
          {isCollapsed ? <ChevronRight className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
      ) : (
        <span className="w-[18px] shrink-0" />
      )}
      {editing ? (
        <span className="flex min-w-0 flex-1 items-center gap-1.5 py-0.5">
          <span className="text-sm">{page.icon || '📄'}</span>
          <input
            ref={(el) => el?.focus()}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              else if (e.key === 'Escape') setEditing(false);
            }}
            className="border-input bg-background min-w-0 flex-1 rounded border px-1 py-0.5 text-sm outline-none"
          />
        </span>
      ) : (
        <button
          type="button"
          // Row click selects the page and, if it has collapsed children,
          // expands them too — the chevron stays as the explicit toggle.
          onClick={() => {
            onSelect(page.id);
            if (hasChildren && isCollapsed) onToggleExpand(page.id);
          }}
          onDoubleClick={startEditing}
          title="Double-click to rename"
          className={cn(
            'flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 py-1 text-left text-sm',
            page.id === selectedId && 'font-medium',
          )}
        >
          <span className="text-sm">{page.icon || '📄'}</span>
          <span className="flex-1 truncate">{page.title || 'Untitled'}</span>
          {page.published_at ? (
            <span
              className="size-1.5 shrink-0 rounded-full bg-emerald-500"
              title="Published — visible to everyone with access to this project"
              aria-label="Published"
            />
          ) : (
            <span
              className="bg-muted-foreground/40 size-1.5 shrink-0 rounded-full"
              title="Draft — only you can see it until you publish"
              aria-label="Draft"
            />
          )}
          {page.is_locked ? (
            <span className="shrink-0" title="Locked — unlock to edit" aria-label="Locked">
              <Lock className="text-muted-foreground size-3" />
            </span>
          ) : null}
        </button>
      )}
      {canWrite ? (
        <PageRowMenu
          page={page}
          onAddChild={onAddChild}
          onRename={startEditing}
          onMoveTo={() => onRequestMove(page)}
          onMoveUp={!flat && !isFirst ? () => onMoveStep(page, 'up') : undefined}
          onMoveDown={!flat && !isLast ? () => onMoveStep(page, 'down') : undefined}
          showKb={showKb}
        />
      ) : null}
    </div>
  );
}

function PageRowMenu({
  page,
  onAddChild,
  onRename,
  onMoveTo,
  onMoveUp,
  onMoveDown,
  showKb,
}: {
  page: WikiPage;
  onAddChild: (parentId: number) => void;
  onRename: () => void;
  onMoveTo: () => void;
  /** Present + enabled only when the page has a sibling above/below. */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  showKb: boolean;
}) {
  const update = pagesHooks.useUpdate();
  const remove = pagesHooks.useRemove();
  const archive = usePageAction((id: string) => pagesApi.archive(id));
  const restore = usePageAction((id: string) => pagesApi.restore(id));
  const publish = usePageAction((id: string) => pagesApi.publish(id));
  const unpublish = usePageAction((id: string) => pagesApi.unpublish(id));
  const patch = (input: Partial<WikiPage>) => update.mutate({ id: page.public_id, input });
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Knowledge-Base management is staff-only infra — hidden from guests.
  const isGuest = useSession((s) => s.role) === 'guest';

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Actions for ${page.title || 'page'}`}
          className="text-muted-foreground hover:text-foreground shrink-0 rounded p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100"
        >
          <MoreHorizontal className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {page.is_archived ? (
          <>
            <DropdownMenuItem onClick={() => restore.mutate(page.public_id)}>
              <RotateCcw className="size-4" /> Restore
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="size-4" /> Delete
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={onRename}>
              <Pencil className="size-4" /> Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddChild(page.id)}>
              <Plus className="size-4" /> Add sub-page
            </DropdownMenuItem>
            {/* Reordering goes through the staff-only reorder endpoint. */}
            {!isGuest && onMoveUp ? (
              <DropdownMenuItem onClick={onMoveUp}>
                <ArrowUp className="size-4" /> Move up
              </DropdownMenuItem>
            ) : null}
            {!isGuest && onMoveDown ? (
              <DropdownMenuItem onClick={onMoveDown}>
                <ArrowDown className="size-4" /> Move down
              </DropdownMenuItem>
            ) : null}
            {!isGuest ? (
              <DropdownMenuItem onClick={onMoveTo}>
                <FolderInput className="size-4" /> Move to…
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onClick={() => patch({ visibility: page.visibility === 'public' ? 'private' : 'public' })}
            >
              {page.visibility === 'public' ? <EyeOff className="size-4" /> : <Eye className="size-4" />} Make{' '}
              {page.visibility === 'public' ? 'private' : 'public'}
            </DropdownMenuItem>
            {!isGuest ? (
              <DropdownMenuItem onClick={() => patch({ is_locked: !page.is_locked })}>
                {page.is_locked ? <Unlock className="size-4" /> : <Lock className="size-4" />}
                {page.is_locked ? 'Unlock' : 'Lock'}
              </DropdownMenuItem>
            ) : null}
            {!showKb ? null : page.kb_excluded && !page.exclude_from_kb ? (
              <DropdownMenuItem disabled>
                <Ban className="size-4" /> Excluded from KB (via a parent)
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={() => patch({ exclude_from_kb: !page.exclude_from_kb })}
              >
                <Ban className="size-4" />{' '}
                {page.exclude_from_kb ? 'Include in Knowledge Base' : 'Exclude from Knowledge Base'}
              </DropdownMenuItem>
            )}
            {!isGuest ? (
              <>
                <DropdownMenuItem
                  onClick={() =>
                    page.published_at
                      ? unpublish.mutate(page.public_id)
                      : publish.mutate(page.public_id)
                  }
                >
                  {page.published_at ? <GlobeLock className="size-4" /> : <Globe className="size-4" />}{' '}
                  {page.published_at ? 'Unpublish' : 'Publish'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => archive.mutate(page.public_id)}
                >
                  <Archive className="size-4" /> Archive
                </DropdownMenuItem>
              </>
            ) : (
              // Guests can't archive (staff infra) but may delete their pages.
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="size-4" /> Delete
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>

    <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{page.title || 'Untitled'}”?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the page. Its sub-pages are kept and moved up one level.
            This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => remove.mutate(page.public_id)}>Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------
function PageEditor({
  project,
  page,
  allPages,
  onAddChild,
  onOpenPage,
}: {
  project: Project;
  page: WikiPage;
  /** All project pages (for the breadcrumb ancestor chain). */
  allPages: WikiPage[];
  onAddChild: (parent: number) => void;
  /** Focus another page by public_id — powers page-embed link navigation. */
  onOpenPage: (publicId: string) => void;
}) {
  const update = pagesHooks.useUpdate();
  // A guest may edit only with a member/lead project role (viewer = read-only).
  const isGuest = useSession((s) => s.role) === 'guest';
  const canWrite = !isGuest || project.my_role === 'member' || project.my_role === 'lead';
  // Project members (for @mentions) is a staff-only endpoint — skip for guests.
  const members: MentionItem[] = rowsOf<ProjectMember>(
    projectMembersHooks.useList({ project: project.id }, !isGuest).data,
  ).map((m) => ({ id: m.member, label: projectMemberName(m) }));
  const pageProvider = useMemo(
    () => makeProjectPageProvider(project.id, onOpenPage, page.public_id),
    [project.id, onOpenPage, page.public_id],
  );
  const archive = usePageAction((id: string) => pagesApi.archive(id));
  const restore = usePageAction((id: string) => pagesApi.restore(id));
  const publish = usePageAction((id: string) => pagesApi.publish(id));
  const unpublish = usePageAction((id: string) => pagesApi.unpublish(id));
  const syncToRag = useSyncSource();
  const remove = pagesHooks.useRemove();
  const [confirmDelete, setConfirmDelete] = useState(false);
  // KB sync/status is a staff-only endpoint — skip the query for guests (403),
  // and when the project's KB sync is off (DEV13-113).
  const showKb = !isGuest && project.kb_sync_enabled;
  const syncStatus = useSourceStatus('projects.wikipage', [page.id], showKb);
  const kbStatus = syncStatus.data?.get(page.id);
  const [title, setTitle] = useState(page.title);
  const [historyOpen, setHistoryOpen] = useState(false);

  const crumbs = useMemo<Crumb[]>(
    () => [
      { label: project.name },
      { label: 'Pages' },
      ...ancestorsOf(allPages, page).map((a) => ({
        label: a.title || 'Untitled',
        icon: <FileText className="size-3.5 shrink-0" />,
        onClick: () => onOpenPage(a.public_id),
      })),
      { label: page.title || 'Untitled', icon: <FileText className="size-3.5 shrink-0" /> },
    ],
    [project.name, allPages, page, onOpenPage],
  );

  const patch = (input: Partial<WikiPage>) => update.mutate({ id: page.public_id, input });

  // View-first: pages open read-only; switch to write mode via "Edit". Locked
  // or archived pages have no Edit affordance (view-only).
  const canEdit = !page.is_locked && !page.is_archived && canWrite;
  const [isEditing, setIsEditing] = useState(false);
  const persistBody = useCallback(
    (value: { json: JSONContent; html: string }, cbs: { onSuccess: () => void; onError: () => void }) =>
      update.mutate(
        {
          id: page.public_id,
          input: { body_json: value.json as unknown as Record<string, unknown>, body: value.html },
        },
        { onSuccess: cbs.onSuccess, onError: cbs.onError },
      ),
    [update, page.public_id],
  );
  const { state: saveState, onChange: onBodyChange, onSave: onBodySave } = useAutosave(persistBody);
  const editorRef = useRef<BlockEditorHandle>(null);
  const finishEditing = () => {
    editorRef.current?.flush();
    setIsEditing(false);
  };

  return (
    <div className="space-y-3">
      {/* Top bar */}
      <div className="flex items-center gap-2">
        <PageBreadcrumbs items={crumbs} />
        <div className="ml-auto flex items-center gap-1.5">
          {canEdit && isEditing ? (
            <>
              <SaveIndicator state={saveState} className="mr-1" />
              <Button
                variant="outline"
                size="sm"
                // Always clickable; flush() is a no-op on a clean doc, so a
                // redundant click never hits the server (same guard as autosave).
                onClick={() => editorRef.current?.flush()}
              >
                Save
              </Button>
              <Button size="sm" onClick={finishEditing}>
                Done
              </Button>
            </>
          ) : canEdit ? (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="size-4" /> Edit
            </Button>
          ) : null}
          {page.published_at ? (
            <Badge className="bg-emerald-600">Live</Badge>
          ) : (
            <Badge variant="secondary" title="Only you can see this page until you publish it">
              Draft
            </Badge>
          )}
          {page.is_locked ? <Badge variant="outline">Locked</Badge> : null}
          {showKb ? <KbSyncBadge status={kbStatus} /> : null}
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Copy link to this page"
            title="Copy link"
            onClick={() => {
              const url = `${window.location.origin}${projectPath(project)}?tab=Pages&page=${page.public_id}`;
              void navigator.clipboard?.writeText(url);
              toast.success('Link copied');
            }}
          >
            <Link2 className="size-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8" aria-label="Page actions">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {!page.is_archived ? (
                <>
                  <DropdownMenuItem onClick={() => onAddChild(page.id)}>
                    <Plus className="size-4" /> Add sub-page
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      patch({ visibility: page.visibility === 'public' ? 'private' : 'public' })
                    }
                  >
                    {page.visibility === 'public' ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    Make {page.visibility === 'public' ? 'private' : 'public'}
                  </DropdownMenuItem>
                  {/* Lock + Publish are staff-only (guests get 403 / no-op). */}
                  {!isGuest ? (
                    <>
                      <DropdownMenuItem onClick={() => patch({ is_locked: !page.is_locked })}>
                        {page.is_locked ? <Unlock className="size-4" /> : <Lock className="size-4" />}
                        {page.is_locked ? 'Unlock' : 'Lock'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          page.published_at
                            ? unpublish.mutate(page.public_id)
                            : publish.mutate(page.public_id)
                        }
                      >
                        {page.published_at ? <GlobeLock className="size-4" /> : <Globe className="size-4" />}{' '}
                        {page.published_at ? 'Unpublish' : 'Publish'}
                      </DropdownMenuItem>
                    </>
                  ) : null}
                  <DropdownMenuItem onClick={() => setHistoryOpen(true)}>
                    <History className="size-4" /> History
                  </DropdownMenuItem>
                  {showKb ? (
                    <DropdownMenuItem
                      onClick={() =>
                        syncToRag.mutate({ entity: 'projects.wikipage', entity_id: page.id })
                      }
                    >
                      <Database className="size-4" />{' '}
                      {kbStatus?.synced ? 'Re-sync to Knowledge Base' : 'Sync to Knowledge Base'}
                    </DropdownMenuItem>
                  ) : null}
                  {!isGuest ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => archive.mutate(page.public_id)}
                      >
                        <Archive className="size-4" /> Archive
                      </DropdownMenuItem>
                    </>
                  ) : (
                    // Guests can't archive (staff infra) but may delete their pages.
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setConfirmDelete(true)}
                      >
                        <Trash2 className="size-4" /> Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              ) : (
                <DropdownMenuItem onClick={() => restore.mutate(page.public_id)}>
                  <RotateCcw className="size-4" /> Restore
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Title + icon on one line */}
      <div className="flex max-w-5xl items-center gap-2">
        <EmojiPicker
          compact
          value={page.icon}
          onChange={(icon) => patch({ icon })}
          placeholder="Page icon"
          disabled={!canEdit || !isEditing}
        />
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== page.title && patch({ title })}
          placeholder="Untitled"
          disabled={!canEdit || !isEditing}
          // `md:text-5xl` overrides Input's base `md:text-sm`; `disabled:` resets
          // keep the title crisp (not faded/greyed) in read-only view mode.
          className="h-auto min-w-0 flex-1 border-0 px-0 text-xl font-bold leading-tight tracking-tight shadow-none focus-visible:ring-0 disabled:cursor-text disabled:opacity-100 md:text-2xl"
        />
      </div>

      {/* Body — capped to a readable content column, not full-page width. */}
      <div className="max-w-5xl">
        {canEdit && isEditing ? (
          <BlockEditor
            ref={editorRef}
            value={page.body_json as unknown as JSONContent}
            html={page.body}
            members={members}
            pageProvider={pageProvider}
            placeholder="Type “/” for commands…"
            onChange={onBodyChange}
            onSave={onBodySave}
          />
        ) : (
          <div className="pt-[15px] pl-1 text-sm">
            <BlockContent
              value={page.body_json as unknown as JSONContent}
              html={page.body}
              pageProvider={pageProvider}
            />
          </div>
        )}
      </div>

      <HistoryDialog page={page} open={historyOpen} onOpenChange={setHistoryOpen} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{page.title || 'Untitled'}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the page. Its sub-pages are kept and moved up one
              level. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate(page.public_id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HistoryDialog({
  page,
  open,
  onOpenChange,
}: {
  page: WikiPage;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const versionsQuery = usePageVersions(open ? page.public_id : null);
  const restoreVersion = usePageAction((vid: number) => pagesApi.restoreVersion(page.public_id, vid));
  const versions = (versionsQuery.data ?? []) as WikiPageVersion[];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Page history</DialogTitle>
        </DialogHeader>
        {versionsQuery.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : versions.length === 0 ? (
          <p className="text-muted-foreground text-sm">No previous versions yet.</p>
        ) : (
          <ul className="divide-y">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center gap-3 py-2 text-sm">
                <span className="text-muted-foreground">v{v.version_number}</span>
                <span className="flex-1 truncate">{v.title || 'Untitled'}</span>
                <span className="text-muted-foreground text-xs">
                  {new Date(v.created).toLocaleString()}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={restoreVersion.isPending}
                  onClick={() =>
                    restoreVersion.mutate(v.id, { onSuccess: () => onOpenChange(false) })
                  }
                >
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
